// @ts-strict-ignore
import type { Selection } from 'd3';
import * as d3 from 'd3';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import type { Literal, Parent } from 'unist';
import { visitParents, SKIP, EXIT, CONTINUE } from 'unist-util-visit-parents';
import type { Visitor } from 'unist-util-visit-parents';

type WrapMarkdownTextOptions = {
    align?: string;
    lineHeight?: number;
    styles?: {
        [key: string]: object;
    };
    verticalAlign?: string;
};
// todo add support for justification left/right/center.
// todo Better to set a width prop on the text? then we can
function wrapMarkdownText(
    text: Selection<SVGTextElement, unknown, SVGElement, undefined>,
    options: WrapMarkdownTextOptions = {},
) {
    text.each(function () {
        const text = d3.select(this);
        let textAlign = options.align;

        if (text.attr('text-anchor') === 'end' && !textAlign) {
            text.attr(
                'x',
                parseFloat(text.attr('x')) - parseFloat(text.attr('width')),
            );
            text.attr('text-anchor', 'start');
            textAlign = 'right';
        }
        if (text.attr('text-anchor') === 'middle' && !textAlign) {
            text.attr(
                'x',
                parseFloat(text.attr('x')) - parseFloat(text.attr('width')) / 2,
            );
            text.attr('text-anchor', 'start');
            textAlign = 'center';
        }
        const syntaxTree = unified().use(remarkParse).parse(text.text()),
            minFontSize = 12,
            width = parseFloat(text.attr('width')) || 0,
            height = parseFloat(text.attr('height')) || 0,
            x = parseFloat(text.attr('x')) || 0,
            y = parseFloat(text.attr('y')) || 0,
            dx = parseFloat(text.attr('dx')) || 0,
            dy = parseFloat(text.attr('dy')) || 0;

        const lineHeight = options.lineHeight || 1.1; // ems
        let currentX = 0;

        text.text(null);

        const stylesStack = [
            {
                'white-space': 'pre', // Needed, or a trailing space is not include in getComputedTextLength
                'font-family':
                    text.style('font-family') ||
                    text.attr('font-family') ||
                    'AvertaStandard',
                'font-weight':
                    text.style('font-weight') ||
                    text.attr('font-weight') ||
                    'normal',
                'font-size':
                    // We cannot support anything else than px
                    parseInt(text.style('font-size')) ||
                    parseInt(text.attr('font-size')) ||
                    16,
                fill: text.style('fill') || text.attr('fill') || 'black',
            },
        ];

        let canDecreaseSize = stylesStack[0]['font-size'] > minFontSize;
        let decreaseSize = false;

        const visitChildrenWithStyle = (node: Parent, style: object) => {
            // Apply style overrides/extras from the options
            let newStyle = style;
            if (options.styles?.[node.type]) {
                newStyle = { ...style, ...options.styles[node.type] };
            }
            stylesStack.push({
                ...stylesStack[stylesStack.length - 1],
                ...newStyle,
            });
            node.children.map((child) => visitParents(child, visitor));
            stylesStack.pop();
        };
        const visitor: Visitor<Parent> = (node) => {
            switch (node.type) {
                case 'strong': {
                    visitChildrenWithStyle(node, {
                        'font-weight': 'bold',
                    });
                    return SKIP;
                }

                case 'text': {
                    const style = stylesStack[stylesStack.length - 1];
                    const result = wrapText(
                        (node as unknown as Literal).value as string,
                        style,
                    );

                    if (result === 'decreaseSize') {
                        decreaseSize = true;
                        return EXIT;
                    }

                    if (result === 'ellipsis') {
                        return EXIT;
                    }

                    break;
                }
                default:
                    break;
            }
            return CONTINUE;
        };

        const widthOfString = (
            tspan: Selection<SVGTSpanElement, unknown, null, undefined>,
        ) => {
            return tspan.node().getComputedTextLength();
        };

        const lines: Array<{
            tspans: Array<Selection<SVGTSpanElement, unknown, null, undefined>>;
            textLength: number;
        }> = [];
        const addLine = () =>
            lines.push({
                tspans: [],
                textLength: 0,
            });
        const addTspan = (style: object) => {
            if (lines.length === 0) {
                addLine();
            }
            const tspan = text
                .append('tspan')
                .attr('x', x + currentX)
                .attr('dx', dx + 'em')
                .attr('y', y)
                .attr('dy', dy + (lines.length - 1) * lineHeight + 'em');
            Object.keys(style).forEach((prop) =>
                tspan.style(prop, style[prop]),
            );
            lines[lines.length - 1].tspans.push(tspan);
            return tspan;
        };

        const wrapText = (textString: string, style: object) => {
            const words = textString.split(/\s+/);
            let line: string[] = [];
            let tspan = addTspan(style);
            let textLength = 0;

            while (words.length) {
                const word = words.shift();
                line.push(word);
                tspan.text(line.join(' '));

                textLength = widthOfString(tspan) || 0;

                if (currentX + textLength > width) {
                    line.pop(); // Maybe break the word somewhere

                    const textHeight =
                        (lines.length + 1) * style['font-size'] * lineHeight;

                    if (textHeight > height) {
                        if (canDecreaseSize) {
                            return 'decreaseSize';
                        }

                        // ellipses is one strategy. Reducing font size is another
                        if (line.length === 0) {
                            line.push('…');
                        } else {
                            line[line.length - 1] = line[
                                line.length - 1
                            ].replace(/.$/, '…');
                        }
                        tspan.text(line.join(' '));

                        tspan.text(line.join(' '));
                        currentX += widthOfString(tspan);

                        const lastWord = ' ' + word;

                        tspan = addTspan(style);

                        for (let i = 0; i < word.length; i++) {
                            tspan.text(lastWord.substring(0, i) + '…');
                            textLength = widthOfString(tspan);
                            if (currentX + textLength > width) {
                                tspan.text(word.substring(0, i - 1) + '…');
                                break;
                            }
                        }
                        lines[lines.length - 1].textLength =
                            currentX + textLength;

                        return 'ellipsis';
                    }

                    addLine();

                    tspan.text(line.join(' '));

                    line = [word];
                    currentX = 0;
                    tspan = addTspan(style).text(word);

                    textLength = widthOfString(tspan) || 0;
                }
                lines[lines.length - 1].textLength = currentX + textLength;
            }
            currentX += textLength;
        };

        const originalFontSize = stylesStack[0]['font-size'];
        do {
            decreaseSize = false;
            visitParents(syntaxTree, visitor);

            if (decreaseSize) {
                while (lines.length) {
                    lines.pop();
                }
                while (stylesStack.length > 1) {
                    stylesStack.pop();
                }
                text.text(null);
                currentX = 0;
                stylesStack[0]['font-size'] -= 1;
                canDecreaseSize = stylesStack[0]['font-size'] >= minFontSize;
                if (!canDecreaseSize) {
                    stylesStack[0]['font-size'] = originalFontSize;
                }
            }
        } while (decreaseSize);

        lines.forEach((line) => {
            let offset = 0;
            if (textAlign === 'right') {
                offset = width - line.textLength;
            }
            if (textAlign === 'center') {
                offset = (width - line.textLength) / 2;
            }
            if (offset) {
                line.tspans.forEach(
                    (
                        tspan: Selection<
                            SVGTSpanElement,
                            unknown,
                            null,
                            undefined
                        >,
                    ) => {
                        tspan.attr('x', parseFloat(tspan.attr('x')) + offset);
                    },
                );
            }
        });

        if (options.verticalAlign === 'center') {
            const dyOffset = -(lines.length - 1) / 2;
            if (dyOffset) {
                lines.forEach((line) => {
                    line.tspans.forEach(
                        (
                            tspan: Selection<
                                SVGTSpanElement,
                                unknown,
                                null,
                                undefined
                            >,
                        ) => {
                            tspan.attr(
                                'dy',
                                parseFloat(tspan.attr('dy')) + dyOffset + 'em',
                            );
                        },
                    );
                });
            }
        }
    });
}

export default wrapMarkdownText;
